import type { Command } from 'commander';
import { unwrapDataResponse } from '@wport/core';
import { personalGet, type PersonalRequestOptions } from '../../../lib/personal-client';
import { resolveContext, type ResolvedContext } from '../../../lib/global-opts';
import { CliError, ExitCode } from '../../../lib/errors';
import { dim, printJson, printTable } from '../../../lib/output';
import { parseFieldsList, pickPaths } from '../../../lib/path-utils';
import { PERSONAL_RESUMES_BASE, type ResumeListData, type ResumeQuota } from '@wport/core';

interface ListFlags {
	fields?: string;
	minimal?: boolean;
}

/** 同 design doc §3.4「list 預設欄位」，也是表格顯示的欄位集合（is_disabled/disable_reason 不列入）。 */
const MINIMAL_LIST_FIELDS = ['enc_id', 'name', 'updated_at', 'is_complete', 'is_published'];

/** 格式化沿 enterprise/keys/list.ts 的 formatDate 模式（每個 list 命令各自複製一份，檔案小而聚焦）。 */
function formatDate(value: string | null | undefined): string {
	return value ? String(value).slice(0, 10) : '';
}

/** 表格尾行摘要：`{used}/{max} used, can create.` 或 `..., cannot create (reason_code).`（無 reason_code 則省略括號）。 */
function formatQuotaLine(quota: ResumeQuota): string {
	const status = quota.can_create ? 'can create' : `cannot create${quota.reason_code ? ` (${quota.reason_code})` : ''}`;
	return `${quota.used}/${quota.max_resumes} used, ${status}.`;
}

/**
 * `resumes list` 共用抓取邏輯：GET /api/v1/personal/resumes → unwrap `{resumes, quota}`。
 * `resumes export`（export.ts）也需要先拿到全部 enc_id 才能逐份 view，故抽成獨立函式共用，
 * 而不是各自重複一份 personalGet + unwrapDataResponse。
 */
export async function fetchResumeList(opts: PersonalRequestOptions): Promise<ResumeListData> {
	const { body } = await personalGet(opts, PERSONAL_RESUMES_BASE);
	return unwrapDataResponse<ResumeListData>(body);
}

/**
 * `resumes list` 核心。data 為 `{resumes, quota}`（非陣列，R1 端點形狀），故不能用
 * `unwrapDataArray`——quota 是 Agent 判斷能否 create 的關鍵資訊，一律隨 resumes 一起印出
 * （--fields/--minimal 只投影 resumes 陣列，quota 原樣保留，DR-10）。
 */
export async function runResumesList(ctx: ResolvedContext, flags: ListFlags): Promise<void> {
	if (flags.fields && flags.minimal) {
		throw new CliError('Use either --fields or --minimal, not both', ExitCode.InvalidArgument);
	}
	const opts: PersonalRequestOptions = { baseUrl: ctx.baseUrl, locale: ctx.locale, timeoutMs: ctx.timeoutMs };
	const data = await fetchResumeList(opts);

	const projection = flags.minimal ? MINIMAL_LIST_FIELDS : flags.fields ? parseFieldsList(flags.fields) : undefined;
	if (projection || ctx.format === 'json') {
		printJson(projection ? { ...data, resumes: data.resumes.map((r) => pickPaths(r, projection)) } : data);
		return;
	}

	printTable(
		data.resumes,
		[
			{ header: 'ENC_ID', value: (r) => r.enc_id.slice(0, 14) },
			{ header: 'NAME', value: (r) => r.name, maxWidth: 24 },
			{ header: 'UPDATED', value: (r) => formatDate(r.updated_at), maxWidth: 12 },
			{ header: 'COMPLETE', value: (r) => (r.is_complete ? 'yes' : 'no') },
			{ header: 'PUBLISHED', value: (r) => (r.is_published ? 'yes' : 'no') },
		],
		ctx.color
	);
	process.stdout.write(dim(formatQuotaLine(data.quota), ctx.color) + '\n');
}

export function registerPersonalResumesList(parent: Command): void {
	parent
		.command('list')
		.description('List your personal resumes')
		.option('--fields <list>', 'output selected fields as JSON (comma-separated dotted paths, applied to each resume)')
		.option('--minimal', `output only ${MINIMAL_LIST_FIELDS.join(',')} as JSON`)
		.action(async (flags: ListFlags, command: Command) => {
			await runResumesList(resolveContext(command), flags);
		});
}

// Internal helpers exposed for tests. Not part of the public CLI API.
export const __test__ = { formatDate, formatQuotaLine, MINIMAL_LIST_FIELDS };
